import React, { useEffect, useState, useCallback } from "react";
import { api, openAuthedFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, DownloadSimple, Trash, FilePdf, FileXls, FileDoc, FileZip, Image as ImageIcon,
  FileText, UploadSimple, FolderOpen,
} from "@phosphor-icons/react";

const CATEGORIES = ["Catálogos", "Tabelas de Preço", "Documentos", "Imagens", "Outros"];

function fileIcon(ext, ct) {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return { Icon: FilePdf, color: "text-red-600 bg-red-50" };
  if (["xls", "xlsx", "xlsb", "csv"].includes(e)) return { Icon: FileXls, color: "text-green-600 bg-green-50" };
  if (["doc", "docx"].includes(e)) return { Icon: FileDoc, color: "text-blue-600 bg-blue-50" };
  if (["zip", "rar", "7z"].includes(e)) return { Icon: FileZip, color: "text-amber-600 bg-amber-50" };
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(e) || (ct || "").startsWith("image/")) return { Icon: ImageIcon, color: "text-purple-600 bg-purple-50" };
  return { Icon: FileText, color: "text-zinc-600 bg-zinc-100" };
}

function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function MaterialsPage() {
  const { isAdmin } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Catálogos" });
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    const params = cat !== "all" ? { category: cat } : {};
    setMaterials((await api.get("/materials", { params })).data);
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Informe um título."); return; }
    if (!file) { toast.error("Selecione um arquivo."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("category", form.category);
    fd.append("file", file);
    try {
      await api.post("/materials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material publicado.");
      setOpen(false); setForm({ title: "", description: "", category: "Catálogos" }); setFile(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Excluir "${m.title}"?`)) return;
    await api.delete(`/materials/${m.id}`); toast.success("Excluído."); load();
  };

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Central de Materiais</h1>
          <p className="text-muted-foreground mt-1">Catálogos, tabelas, documentos e imagens para download.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setOpen(true)} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-material-btn">
            <Plus size={18} weight="bold" /> Adicionar Material
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`material-cat-${c}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${cat === c ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-border hover:bg-zinc-50"}`}>
            {c === "all" ? "Todos" : c}
          </button>
        ))}
      </div>

      {materials.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-lg">
          <FolderOpen size={44} className="mx-auto mb-3 text-zinc-300" />
          Nenhum material {cat !== "all" ? "nesta categoria" : "publicado ainda"}.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {materials.map((m) => {
            const { Icon, color } = fileIcon(m.ext, m.content_type);
            return (
              <Card key={m.id} className="p-5 shadow-none border-border flex flex-col" data-testid={`material-card-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
                    <Icon size={26} weight="fill" />
                  </div>
                  {isAdmin && (
                    <button onClick={() => remove(m)} className="text-zinc-400 hover:text-destructive" data-testid={`delete-material-${m.id}`}>
                      <Trash size={18} />
                    </button>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-zinc-900 mt-3 leading-snug">{m.title}</h3>
                {m.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{m.description}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="secondary" className="text-xs">{m.category}</Badge>
                  <span className="text-xs text-zinc-400 uppercase">{m.ext}</span>
                  <span className="text-xs text-zinc-400">· {humanSize(m.size)}</span>
                </div>
                <Button variant="outline" className="mt-4 w-full gap-2" data-testid={`download-material-${m.id}`}
                  onClick={() => openAuthedFile(`/materials/${m.id}/download`, m.filename)}>
                  <DownloadSimple size={18} weight="bold" /> Baixar
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar material</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Catálogo DUALT Julho 2026" data-testid="material-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="material-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Descrição (opcional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Arquivo</Label>
              <label className="flex items-center gap-3 border border-dashed border-border rounded-md px-4 py-3 cursor-pointer hover:bg-zinc-50">
                <UploadSimple size={20} className="text-accent" />
                <span className="text-sm text-zinc-600 truncate">{file ? file.name : "Selecionar arquivo (máx 25MB)"}</span>
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="material-file" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={uploading} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-material-btn">
              {uploading ? "Enviando..." : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
